import { useState, useCallback } from 'react';
import { AlertTriangle, Check, FlaskConical, Info } from 'lucide-react';
import LabBench from '../../LabBench';
import { TestTubeRack } from '@/components/lab';
import InfoBox from '../../InfoBox';

const stepInfoContent: Record<number, { title: string; content: string }> = {
  1: {
    title: 'Laboratory Safety for Anion Analysis',
    content: `Safety is paramount when working with chemicals used in anion analysis. Concentrated H₂SO₄ is highly corrosive and can cause severe burns - always add acid to water, never the reverse. Silver nitrate (AgNO₃) permanently stains skin and clothing through a photochemical reaction. Barium compounds are toxic if ingested. Methylene chloride is volatile and should only be used in a fume hood. Proper PPE (safety goggles, lab coat, gloves) must be worn throughout the experiment.`
  },
  2: {
    title: 'Understanding Anions in Chemistry',
    content: `Anions are negatively charged ions formed when atoms gain electrons. Non-metals typically form anions due to their high electronegativity and electron affinity. In qualitative analysis, we identify specific anions through characteristic reactions: carbonate releases CO₂ gas with acids, halides form colored precipitates with silver ions, and sulfate forms a white precipitate with barium ions. Each test exploits unique chemical properties of the target anion.`
  },
  3: {
    title: 'Preparing Test Tubes for Analysis',
    content: `Proper labeling and organization of test tubes is essential for systematic anion analysis. Each tube should be clearly marked with the anion symbol (CO₃, Cl, I, SO₄) to avoid confusion during testing. Clean, dry tubes ensure accurate results by preventing contamination. The four anions we test represent major groups: polyatomic oxyanions (carbonate, sulfate) and simple halides (chloride, iodide), each requiring different confirmation tests.`
  }
};

interface Part1SetupProps {
  onStepComplete?: (stepId: string) => void;
  currentStep?: number;
}

export default function Part1Setup({ onStepComplete, currentStep = 1 }: Part1SetupProps) {
  const [showInfo, setShowInfo] = useState<number | null>(null);
  const [safetyAcknowledged, setSafetyAcknowledged] = useState(false);
  const [anionsLearned, setAnionsLearned] = useState(false);
  const [tubesLabeled, setTubesLabeled] = useState(false);

  const handleAcknowledgeSafety = useCallback(() => {
    setSafetyAcknowledged(true);
    if (onStepComplete) onStepComplete('setup-1');
  }, [onStepComplete]);

  const handleLearnAnions = useCallback(() => {
    if (safetyAcknowledged) {
      setAnionsLearned(true);
      if (onStepComplete) onStepComplete('setup-2');
    }
  }, [safetyAcknowledged, onStepComplete]);

  const handleLabelTubes = useCallback(() => {
    if (anionsLearned) {
      setTubesLabeled(true);
      if (onStepComplete) onStepComplete('setup-3');
    }
  }, [anionsLearned, onStepComplete]);

  // Test tube data for labeled tubes
  const labeledTubes = tubesLabeled ? [
    { id: 'co3', fillLevel: 60, fillColor: '#F5F5F5', label: 'CO₃' },
    { id: 'cl', fillLevel: 60, fillColor: '#F5F5F5', label: 'Cl' },
    { id: 'i', fillLevel: 60, fillColor: '#FFFDE7', label: 'I' },
    { id: 'so4', fillLevel: 60, fillColor: '#F5F5F5', label: 'SO₄' },
  ] : [];

  return (
    <LabBench
      experimentName="Anion Analysis Setup"
      completedCount={[safetyAcknowledged, anionsLearned, tubesLabeled].filter(Boolean).length}
      totalSteps={3}
      equipment={['Test tube rack', 'Test tubes', 'Anion solutions']}
      labMode="guided"
      currentStep={currentStep}
    >
      <div className="anion-setup">
        {/* Safety Warning */}
        {!safetyAcknowledged && (
          <div className="safety-warning-panel">
            <div className="warning-header">
              <AlertTriangle size={24} />
              <h3>Safety Warning - Chemical Handling</h3>
            </div>
            <ul className="safety-list">
              <li>Wear <strong>safety goggles</strong> at all times</li>
              <li>Concentrated <strong>H₂SO₄</strong> is very corrosive - handle with extreme care</li>
              <li>Work in <strong>fume hood</strong> when using H₂SO₄</li>
              <li><strong>Methylene chloride</strong> is volatile - avoid inhalation</li>
              <li><strong>Ba(OH)2</strong> is toxic - do not ingest</li>
              <li><strong>AgNO₃</strong> stains skin - avoid contact</li>
              <li>Wash hands <strong>thoroughly</strong> after the experiment</li>
            </ul>
            <button className="acknowledge-btn" onClick={handleAcknowledgeSafety}>
              <Check size={16} />
              I Understand and Acknowledge
            </button>
          </div>
        )}

        {/* Learn about Anions */}
        {safetyAcknowledged && !anionsLearned && (
          <div className="info-panel">
            <div className="info-header">
              <Info size={24} />
              <h3>Understanding Anions</h3>
            </div>
            <div className="anion-info">
              <p>
                An <strong>anion</strong> is a negatively charged ion formed when an atom
                gains one or more electrons. Non-metals typically form anions because they
                have high electron affinity.
              </p>
              <div className="anion-grid">
                <div className="anion-card" style={{ borderColor: '#9E9E9E' }}>
                  <span className="anion-symbol">CO<sub>3</sub><sup>2-</sup></span>
                  <span className="anion-name">Carbonate</span>
                  <span className="anion-desc">Reacts with acids - gas evolved</span>
                </div>
                <div className="anion-card" style={{ borderColor: '#BDBDBD' }}>
                  <span className="anion-symbol">Cl<sup>-</sup></span>
                  <span className="anion-name">Chloride</span>
                  <span className="anion-desc">White AgCl precipitate</span>
                </div>
                <div className="anion-card" style={{ borderColor: '#FFEB3B' }}>
                  <span className="anion-symbol">I<sup>-</sup></span>
                  <span className="anion-name">Iodide</span>
                  <span className="anion-desc">Yellow AgI / violet I2</span>
                </div>
                <div className="anion-card" style={{ borderColor: '#E0E0E0' }}>
                  <span className="anion-symbol">SO<sub>4</sub><sup>2-</sup></span>
                  <span className="anion-name">Sulfate</span>
                  <span className="anion-desc">White BaSO₄ precipitate</span>
                </div>
              </div>
              <div className="equation-box">
                <span>Example: Cl + e<sup>-</sup> → Cl<sup>-</sup></span>
              </div>
            </div>
            <button className="action-btn" onClick={handleLearnAnions}>
              <Check size={16} />
              I Understand - Continue
            </button>
          </div>
        )}

        {/* Label Test Tubes */}
        {anionsLearned && !tubesLabeled && (
          <div className="setup-panel">
            <div className="info-header">
              <FlaskConical size={24} />
              <h3>Prepare Test Tubes</h3>
            </div>
            <p className="instruction-text">
              Label 4 test tubes with the anion symbols: <strong>CO₃, Cl, I, SO₄</strong>
            </p>
            <div className="empty-rack">
              <TestTubeRack
                tubes={[
                  { id: '1', fillLevel: 0, fillColor: 'transparent', label: '?' },
                  { id: '2', fillLevel: 0, fillColor: 'transparent', label: '?' },
                  { id: '3', fillLevel: 0, fillColor: 'transparent', label: '?' },
                  { id: '4', fillLevel: 0, fillColor: 'transparent', label: '?' },
                ]}
              />
            </div>
            <button className="action-btn" onClick={handleLabelTubes}>
              <Check size={16} />
              Label Test Tubes
            </button>
          </div>
        )}

        {/* Labeled tubes display */}
        {tubesLabeled && (
          <div className="complete-panel">
            <div className="success-header">
              <Check size={24} />
              <h3>Setup Complete!</h3>
            </div>
            <p>Your test tubes are labeled and filled with anion solutions:</p>
            <div className="labeled-rack">
              <TestTubeRack tubes={labeledTubes} />
            </div>
            <div className="status-message success">
              Ready to begin anion confirmation tests. Proceed to Part 2.
            </div>
          </div>
        )}

        {/* Theory Box */}
        <div className="theory-box">
          <h4>Qualitative Analysis of Anions</h4>
          <p>
            Anion analysis involves specific tests that produce characteristic reactions:
            gas evolution (CO₂ from carbonates), precipitate formation (AgCl, AgI, BaSO₄),
            and color changes (I2 in organic solvents).
          </p>
        </div>

        {/* Info Buttons */}
        <div className="info-buttons-row">
          {[1, 2, 3].map((stepId) => (
            stepInfoContent[stepId] && (
              <button
                key={stepId}
                className="step-info-btn"
                onClick={(e) => { e.stopPropagation(); setShowInfo(stepId); }}
                title={stepInfoContent[stepId].title}
              >
                ?
              </button>
            )
          ))}
        </div>

        {showInfo && stepInfoContent[showInfo] && (
          <InfoBox
            step={showInfo}
            experimentId="exp3"
            stepInfo={stepInfoContent[showInfo]}
            onClose={() => setShowInfo(null)}
          />
        )}

        <style>{`
          .anion-setup {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 24px;
            padding: 20px;
            width: 100%;
            max-width: 650px;
          }

          .safety-warning-panel {
            width: 100%;
            background: rgba(255, 90, 54, 0.1);
            border: 2px solid #FF5A36;
            border-radius: 12px;
            padding: 20px;
          }

          .warning-header {
            display: flex;
            align-items: center;
            gap: 12px;
            color: #FF5A36;
            margin-bottom: 16px;
          }

          .warning-header h3 {
            margin: 0;
            font-size: 16px;
          }

          .safety-list {
            margin: 0 0 20px 0;
            padding-left: 24px;
            font-size: 13px;
            color: var(--text-primary);
            line-height: 1.8;
          }

          .safety-list strong {
            color: #FF5A36;
          }

          .acknowledge-btn {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 12px 24px;
            background: #FF5A36;
            color: white;
            border: none;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s;
          }

          .acknowledge-btn:hover {
            background: #E04A26;
            transform: translateY(-2px);
          }

          .info-panel, .setup-panel, .complete-panel {
            width: 100%;
            background: var(--bg-elevated);
            border-radius: 12px;
            padding: 20px;
          }

          .info-header, .success-header {
            display: flex;
            align-items: center;
            gap: 12px;
            color: var(--primary);
            margin-bottom: 16px;
          }

          .info-header h3, .success-header h3 {
            margin: 0;
            font-size: 16px;
          }

          .success-header {
            color: #00A86B;
          }

          .anion-info p {
            font-size: 13px;
            color: var(--text-primary);
            line-height: 1.6;
            margin-bottom: 16px;
          }

          .anion-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 12px;
            margin-bottom: 16px;
          }

          .anion-card {
            display: flex;
            flex-direction: column;
            align-items: center;
            padding: 12px;
            background: var(--bg-dark);
            border-radius: 8px;
            border-left: 4px solid;
          }

          .anion-symbol {
            font-size: 16px;
            font-weight: 700;
            color: var(--text-primary);
          }

          .anion-name {
            font-size: 12px;
            color: var(--text-secondary);
            margin-top: 4px;
          }

          .anion-desc {
            font-size: 11px;
            color: var(--text-muted);
            font-style: italic;
            text-align: center;
          }

          .equation-box {
            text-align: center;
            padding: 12px;
            background: var(--bg-dark);
            border-radius: 8px;
            font-family: 'Times New Roman', serif;
            font-size: 14px;
            color: var(--text-secondary);
            margin-bottom: 16px;
          }

          .action-btn {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 12px 24px;
            background: var(--primary);
            color: white;
            border: none;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s;
          }

          .action-btn:hover {
            background: var(--primary-dark);
            transform: translateY(-2px);
          }

          .instruction-text {
            font-size: 13px;
            color: var(--text-primary);
            margin-bottom: 16px;
          }

          .empty-rack, .labeled-rack {
            display: flex;
            justify-content: center;
            margin: 20px 0;
          }

          .status-message {
            padding: 12px 16px;
            border-radius: 8px;
            font-size: 13px;
            text-align: center;
          }

          .status-message.success {
            background: rgba(0, 168, 107, 0.1);
            color: #00A86B;
          }

          .theory-box {
            width: 100%;
            background: var(--bg-elevated);
            padding: 16px 20px;
            border-radius: 12px;
          }

          .theory-box h4 {
            margin: 0 0 8px 0;
            font-size: 13px;
            color: var(--text-secondary);
          }

          .theory-box p {
            margin: 0;
            font-size: 12px;
            color: var(--text-primary);
            line-height: 1.6;
          }

          .info-buttons-row {
            display: flex;
            gap: 8px;
            justify-content: center;
          }

          .step-info-btn {
            width: 18px;
            height: 18px;
            border-radius: 50%;
            background: var(--accent);
            color: var(--bg-dark);
            font-size: 11px;
            font-weight: 700;
            border: none;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            transition: transform 0.15s;
          }
          .step-info-btn:hover {
            transform: scale(1.1);
          }
        `}</style>
      </div>
    </LabBench>
  );
}
